import { Layout, Menu } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UploadOutlined } from '@ant-design/icons';
import {
  ZinAdmin,
  ZinArrowClose,
  ZinAssignments,
  ZinCampaign,
  ZinCandidate,
  ZinClientManagement,
  ZinDashboard,
  ZinEmployerManagement,
  ZinHelp,
  ZinJobs,
  ZinLogoIcon,
  ZinLogoText,
  ZinNetwork,
  ZinOnBoarding,
  ZinReports,
  ZinScheduler,
  ZinSettings,
  ZinSubmission,
  ZinTimesheet,
  ZinPayrollIcon,
  ZinBillingIcon,
} from '../../assets/images';

const { Sider } = Layout;

export const SIDEBAR_WIDTH = 200;
export const SIDEBAR_COLLAPSED_WIDTH = 60;

const iconMap = {
  Dashboard: ZinDashboard,
  Jobs: ZinJobs,
  Candidates: ZinCandidate,
  Submissions: ZinSubmission,
  Network: ZinNetwork,
  ClientManagement: ZinClientManagement,
  Clients: ZinClientManagement,
  clients: ZinClientManagement,
  EmployerManagement: ZinEmployerManagement,
  Employers: ZinEmployerManagement,
  employers: ZinEmployerManagement,
  // Referral reuses the Employer icon — the two are structural mirrors and there
  // is no dedicated referral asset.
  ReferralManagement: ZinEmployerManagement,
  Referral: ZinEmployerManagement,
  Referrals: ZinEmployerManagement,
  referrals: ZinEmployerManagement,
  Scheduler: ZinScheduler,
  Admin: ZinAdmin,
  Reports: ZinReports,
  Settings: ZinSettings,
  Help: ZinHelp,
  onboarding: ZinOnBoarding,
  Onboarding: ZinOnBoarding,
  assignments: ZinAssignments,
  Assignments: ZinAssignments,
  assignment: ZinAssignments,
  Assignment: ZinAssignments,
  timesheet: ZinTimesheet,
  Timesheet: ZinTimesheet,
  billing: ZinBillingIcon,
  Billing: ZinBillingIcon,
  payroll: ZinPayrollIcon,
  Payroll: ZinPayrollIcon,
  campaign: ZinCampaign,
  Campaign: ZinCampaign,
  addTestCase: ZinReports,
  viewTestCase: ZinScheduler,
  manageModules: ZinSettings,
  bulkUpload: UploadOutlined,
  'bulk-upload': UploadOutlined,
};

function parseStoredMenu(storageKey) {
  const rawValue = localStorage.getItem(storageKey);

  if (!rawValue || rawValue === 'undefined' || rawValue === 'null' || rawValue.trim() === '') {
    return [];
  }

  try {
    const parsedValue = JSON.parse(rawValue);
    if (Array.isArray(parsedValue)) return parsedValue;
    if (Array.isArray(parsedValue?.menu)) return parsedValue.menu;
    if (Array.isArray(parsedValue?.menus?.menu)) return parsedValue.menus.menu;

    return [];
  } catch (error) {
    console.error(`Error parsing ${storageKey}:`, error);
    return [];
  }
}

function getSelectedKeys(pathname) {
  const cleanPath = pathname.replace(/^\/+/, '');
  if (!cleanPath) return ['dashboard'];

  const parts = cleanPath.split('/');
  return [cleanPath, parts[0], parts.at(-1)].filter(Boolean);
}

function SidebarIcon({ icon, itemKey }) {
  const iconSrc = itemKey === 'bulk-upload'
    ? UploadOutlined
    : itemKey === 'assignments'
      ? ZinAssignments
      : itemKey === 'employers'
        ? ZinEmployerManagement
        : iconMap[icon];

  if (!iconSrc) return null;
  if (typeof iconSrc !== 'string') {
    const Icon = iconSrc;
    return <Icon className="app-sidebar-menu-icon" aria-hidden="true" />;
  }

  return (
    <img
      src={iconSrc}
      alt=""
      className="app-sidebar-menu-icon"
      aria-hidden="true"
    />
  );
}

function buildMenuItems(menuItems) {
  return menuItems.map((item) => ({
    key: item.key,
    icon: <SidebarIcon icon={item.icon} itemKey={item.key} />,
    label: item.label,
    disabled: item.status === 'inactive',
    children: Array.isArray(item.children) ? buildMenuItems(item.children) : undefined,
  }));
}

function filterMenuByPermission(menuItems) {
  let permissions;
  try {
    permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
  } catch {
    return menuItems;
  }
  const permissionEntries = Object.entries(permissions).reduce((lookup, [key, value]) => ({
    ...lookup,
    [String(key).trim().toLowerCase()]: value,
  }), {});
  const permissionKeys = Object.keys(permissionEntries);
  // The MENU's spelling and the PERMISSION's spelling are not guaranteed to
  // match: the backend lists the menu item as "timesheet" but keys its
  // permission "timesheets". An exact-only lookup found nothing, left `view`
  // undefined, and the `view !== undefined` guard below then treated an
  // explicit view:0 as "unconfigured" and kept the item visible. Fall back to
  // a singular/plural tolerant match so a deny is never read as unconfigured.
  const lookupAccess = (key) => permissionEntries[key]
    ?? permissionEntries[permissionKeys.find((pk) => pk === `${key}s` || `${pk}s` === key) ?? ''];

  return menuItems.flatMap((item) => {
    const candidates = [item.key, item.label].map((value) => String(value ?? '').trim().toLowerCase());
    const access = candidates.map(lookupAccess).find(Boolean);
    const view = access?.view;
    if (view !== undefined && view !== null && ![true, 1, '1'].includes(view)) return [];
    const children = Array.isArray(item.children) ? filterMenuByPermission(item.children) : undefined;
    return [{ ...item, children }];
  });
}

function menuKeys(menuItems) {
  return new Set(menuItems.flatMap((item) => [
    String(item?.key ?? '').trim().toLowerCase(),
    ...(Array.isArray(item?.children) ? [...menuKeys(item.children)] : []),
  ]).filter(Boolean));
}

function permissionEnabled(value) {
  return [true, 1, '1'].includes(value);
}

function menuLabel(key) {
  return String(key)
    .split(/[\s_\-/]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

// The login response can contain a freshly configured module in
// menuPermission before the saved menu structure includes it. Reconcile the
// two sources so any explicitly view-enabled module remains reachable without
// requiring a second backend/menu-structure update.
function withPermissionBackedItems(menuItems) {
  let permissions;
  try {
    permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
  } catch {
    return menuItems;
  }
  if (!permissions || typeof permissions !== 'object' || Array.isArray(permissions)) {
    return menuItems;
  }

  const existingKeys = menuKeys(menuItems);
  const missingItems = Object.entries(permissions).flatMap(([rawKey, access]) => {
    const key = String(rawKey).trim().toLowerCase();
    if (!key || key.includes('/') || existingKeys.has(key) || !permissionEnabled(access?.view)) {
      return [];
    }
    existingKeys.add(key);
    return [{ key, icon: key, label: menuLabel(key), status: 'active' }];
  });

  return [...menuItems, ...missingItems];
}

function withBulkUploadItem(menuItems) {
  const hasTesterMenu = menuItems.some((item) => item.key === 'addTestCase');
  const hasBulkUpload = menuItems.some((item) => item.key === 'bulk-upload');
  if (!hasTesterMenu || hasBulkUpload) return menuItems;

  const nextItems = [...menuItems];
  const manageIndex = nextItems.findIndex((item) => item.key === 'manageModules');
  nextItems.splice(manageIndex >= 0 ? manageIndex + 1 : nextItems.length, 0, {
    key: 'bulk-upload',
    icon: 'bulk-upload',
    label: 'Bulk upload',
    status: 'active',
  });
  return nextItems;
}

function hasClientPermission() {
  try {
    const permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const client = permissions?.client ?? permissions?.clients;
    if (!client || typeof client !== 'object') return false;
    return ['view', 'add', 'edit'].some((key) => Number(client[key]) === 1 || client[key] === true || client[key] === '1');
  } catch {
    return false;
  }
}

function hasEmployerPermission() {
  try {
    const permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const employer = permissions?.employers ?? permissions?.employer;
    if (!employer || typeof employer !== 'object') return false;
    return ['view', 'add', 'edit'].some((key) => Number(employer[key]) === 1 || employer[key] === true || employer[key] === '1');
  } catch {
    return false;
  }
}

function withClientManagementItem(menuItems) {
  const hasClients = menuItems.some((item) => item.key === 'clients' || item.key === 'client');
  if (hasClients || !hasClientPermission()) return menuItems;

  const nextItems = [...menuItems];
  const jobsIndex = nextItems.findIndex((item) => item.key === 'jobs');
  nextItems.splice(jobsIndex >= 0 ? jobsIndex : nextItems.length, 0, {
    key: 'clients',
    icon: 'ClientManagement',
    label: 'Client Management',
    status: 'active',
  });
  return nextItems;
}

function withEmployerManagementItem(menuItems) {
  const hasEmployers = menuItems.some((item) => item.key === 'employers' || item.key === 'employer');
  const hasClients = menuItems.some((item) => item.key === 'clients' || item.key === 'client');
  const hasManagementAccess = hasEmployerPermission() || hasClients || hasClientPermission();
  if (hasEmployers || !hasManagementAccess) return menuItems;

  const nextItems = [...menuItems];
  const jobsIndex = nextItems.findIndex((item) => item.key === 'jobs');
  nextItems.splice(jobsIndex >= 0 ? jobsIndex : nextItems.length, 0, {
    key: 'employers',
    icon: 'EmployerManagement',
    label: 'Employer Management',
    status: 'active',
  });
  return nextItems;
}

function hasReferralPermission() {
  try {
    const permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const referral = permissions?.referrals ?? permissions?.referral;
    if (!referral || typeof referral !== 'object') return false;
    return ['view', 'add', 'edit'].some((key) => Number(referral[key]) === 1 || referral[key] === true || referral[key] === '1');
  } catch {
    return false;
  }
}

// Referral Management mirrors Employer Management: a top-level item linking to
// /referrals, injected right after Employers so the two sit together. Shown when
// the role can reach referrals or the same management area Employer uses.
function withReferralManagementItem(menuItems) {
  const hasReferrals = menuItems.some((item) => item.key === 'referrals' || item.key === 'referral');
  const hasEmployers = menuItems.some((item) => item.key === 'employers' || item.key === 'employer');
  const hasManagementAccess = hasReferralPermission() || hasEmployerPermission() || hasEmployers || hasClientPermission();
  if (hasReferrals || !hasManagementAccess) return menuItems;

  const nextItems = [...menuItems];
  const anchorIndex = nextItems.findIndex((item) => item.key === 'employers' || item.key === 'employer');
  const jobsIndex = nextItems.findIndex((item) => item.key === 'jobs');
  const insertAt = anchorIndex >= 0 ? anchorIndex + 1 : (jobsIndex >= 0 ? jobsIndex : nextItems.length);
  nextItems.splice(insertAt, 0, {
    key: 'referrals',
    icon: 'ReferralManagement',
    label: 'Referral Management',
    status: 'active',
  });
  return nextItems;
}

function hasFinancePermission(module) {
  try {
    const permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const entry = permissions?.[module];
    return entry && ['view', 'add', 'edit'].some((key) => Number(entry[key]) === 1 || entry[key] === true || entry[key] === '1');
  } catch {
    return false;
  }
}

function withFinanceItems(menuItems) {
  const nextItems = [...menuItems];
  const assignmentsIndex = nextItems.findIndex((item) => item.key === 'assignments');
  let insertAt = assignmentsIndex >= 0 ? assignmentsIndex + 1 : nextItems.length;
  for (const [key, label] of [['billing', 'Billing'], ['payroll', 'Payroll']]) {
    if (!nextItems.some((item) => item.key === key) && hasFinancePermission(key)) {
      nextItems.splice(insertAt, 0, { key, icon: key, label, status: 'active' });
      insertAt += 1;
    }
  }
  return nextItems;
}

// The vendor concept has been replaced by Employer — drop any lingering
// "Vendors" entry (e.g. a stored menu from before this change) so it never shows
// while the DB menu catches up on the next login.
function withoutVendorItems(menuItems) {
  return menuItems
    .filter((item) => {
      const key = String(item.key ?? '').toLowerCase();
      const label = String(item.label ?? '').toLowerCase();
      return !key.includes('vendor') && label !== 'vendors';
    })
    .map((item) => (Array.isArray(item.children)
      ? { ...item, children: withoutVendorItems(item.children) }
      : item));
}

function getRoleMenuSnapshot() {
  return [
    localStorage.getItem('menu') ?? '',
    localStorage.getItem('menuPermission') ?? '',
    localStorage.getItem('roleId') ?? '',
    localStorage.getItem('userId') ?? '',
  ].join('::');
}

export default function Sidebar({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);
  const [roleMenuSnapshot, setRoleMenuSnapshot] = useState(() => getRoleMenuSnapshot());
  const selectedKeys = useMemo(() => getSelectedKeys(location.pathname), [location.pathname]);

  // Sidebar menu data is written during login. React to login/logout and
  // cross-tab localStorage changes so the visible navigation always belongs to
  // the currently authenticated role instead of the first user that mounted
  // this Sidebar instance.
  useEffect(() => {
    const refreshRoleMenu = () => setRoleMenuSnapshot(getRoleMenuSnapshot());
    window.addEventListener('auth:login', refreshRoleMenu);
    window.addEventListener('auth:logout', refreshRoleMenu);
    window.addEventListener('storage', refreshRoleMenu);
    return () => {
      window.removeEventListener('auth:login', refreshRoleMenu);
      window.removeEventListener('auth:logout', refreshRoleMenu);
      window.removeEventListener('storage', refreshRoleMenu);
    };
  }, []);

  useEffect(() => {
    if (selectedKeys.length > 0) {
      localStorage.setItem('activeKey', selectedKeys[0]);
    }
  }, [selectedKeys]);

  const items = useMemo(() => {
    const storedMenu = withoutVendorItems(parseStoredMenu('menu'));
    const knownMenu = withFinanceItems(withBulkUploadItem(
      withReferralManagementItem(withEmployerManagementItem(withClientManagementItem(storedMenu))),
    ));
    const augmentedMenu = withPermissionBackedItems(knownMenu);
    return buildMenuItems(filterMenuByPermission(augmentedMenu));
  }, [roleMenuSnapshot]);

  function handleToggle() {
    if (!collapsed) {
      setOpenKeys([]);
    }

    onToggle();
  }

  function handleMenuClick({ key }) {
    navigate(`/${key}`);
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={SIDEBAR_WIDTH}
      collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
      className="app-sidebar"
    >
      <div className="app-sidebar-panel">
        <div className="app-sidebar-brand">
          <div className="app-sidebar-logo">
            <img src={ZinLogoIcon} alt="" className="app-sidebar-logo-icon" />
            <img src={ZinLogoText} alt="Zinnext" className="app-sidebar-logo-text" />
          </div>
        </div>

        <button
          type="button"
          className="app-sidebar-toggle"
          onClick={handleToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <img src={ZinArrowClose} alt="" aria-hidden="true" />
        </button>

        <div className="app-sidebar-body">
          <Menu
            mode="inline"
            theme="dark"
            selectedKeys={selectedKeys}
            openKeys={collapsed ? [] : openKeys}
            items={items}
            onClick={handleMenuClick}
            onOpenChange={setOpenKeys}
            className="app-sidebar-menu"
          />
        </div>
      </div>
    </Sider>
  );
}
